import { mapTypeToStatsScope, getStatScopeOption } from '../constants/statScopes';
import { computeScopeProgress } from './progressStats';

const PLAY_MODE_LABELS = {
	monthly: 'Monthly',
	annual: 'Annual',
	decennial: 'Decennial',
};

function playModeLabel(playMode) {
	return PLAY_MODE_LABELS[playMode] || 'Monthly';
}

const MONTH_NAMES_LONG = [
	'January', 'February', 'March', 'April', 'May', 'June',
	'July', 'August', 'September', 'October', 'November', 'December',
];

function toEastCoastDate(date = new Date()) {
	return new Date(date.toLocaleString('en-US', { timeZone: 'America/New_York' }));
}

function monthYearKeysForCalendarYear(year) {
	return MONTH_NAMES_LONG.map((m) => `${m}-${year}`);
}

export function monthYearKeysForCalendarYearContaining(date = new Date()) {
	const y = toEastCoastDate(date).getFullYear();
	return monthYearKeysForCalendarYear(y);
}

export function monthYearKeysForCalendarDecadeContaining(date = new Date()) {
	const y = toEastCoastDate(date).getFullYear();
	const start = Math.floor(y / 10) * 10;
	const keys = [];
	for (let year = start; year <= start + 9; year++) {
		keys.push(...monthYearKeysForCalendarYear(year));
	}
	return keys;
}

/**
 * Region IDs logged within a comparison window.
 * @param {'monthly' | 'annual' | 'decennial'} window
 */
export function regionIdsForWindow(
	window,
	{ visitedStates = [], stateTiers = {}, stateSightings = {}, currentMonthYear },
) {
	const hasSightings = stateSightings && Object.keys(stateSightings).length > 0;

	if (window === 'monthly') {
		if (hasSightings) {
			return Object.keys(stateSightings).filter((sid) =>
				(stateSightings[sid] || []).includes(currentMonthYear),
			);
		}
		return Array.isArray(visitedStates) ? visitedStates : [];
	}

	if (window === 'annual') {
		if (!hasSightings) return [];
		const yearKeys = new Set(monthYearKeysForCalendarYearContaining());
		return Object.keys(stateSightings).filter((sid) =>
			(stateSightings[sid] || []).some((m) => yearKeys.has(m)),
		);
	}

	if (window === 'decennial') {
		if (!hasSightings) return [];
		const decadeKeys = new Set(monthYearKeysForCalendarDecadeContaining());
		return Object.keys(stateSightings).filter((sid) =>
			(stateSightings[sid] || []).some((m) => decadeKeys.has(m)),
		);
	}

	return [];
}

/**
 * Region IDs for the user's active play mode (matches map coloring / GET /game aggregation).
 * @param {'monthly' | 'annual' | 'decennial'} playMode
 */
export function regionIdsForActivePlayMode(
	playMode,
	visitedStates,
	stateTiers,
	stateSightings,
	currentMonthYear,
) {
	if (playMode === 'annual' || playMode === 'decennial') {
		const tierIds = Object.keys(stateTiers || {});
		if (tierIds.length > 0) return tierIds;
		return regionIdsForWindow(playMode, {
			visitedStates,
			stateTiers,
			stateSightings,
			currentMonthYear,
		});
	}
	return regionIdsForWindow('monthly', {
		visitedStates,
		stateTiers,
		stateSightings,
		currentMonthYear,
	});
}

/**
 * Scope stats for the active play mode and map region toggle.
 */
export function computeActivePlayModeScopeStats({
	mapType,
	playMode,
	visitedStates,
	stateTiers,
	stateSightings,
	currentMonthYear,
	denominators,
}) {
	const scope = mapTypeToStatsScope(mapType);
	const regionIds = regionIdsForActivePlayMode(
		playMode,
		visitedStates,
		stateTiers,
		stateSightings,
		currentMonthYear,
	);
	const stats = computeScopeProgress(regionIds, scope, denominators);
	return {
		scope,
		label: getStatScopeOption(scope).name,
		playMode,
		...stats,
	};
}

/**
 * Stats for other time windows (when stateSightings history allows).
 * @returns {Array<{ window: string, windowLabel: string, count: number, denominator: number, percent: string, percentNumber: number }>}
 */
export function computeAlternateWindowSummaries({
	mapType,
	playMode,
	visitedStates,
	stateTiers,
	stateSightings,
	currentMonthYear,
	windowRegionUnions,
	denominators,
}) {
	const scope = mapTypeToStatsScope(mapType);
	const hasServerUnions =
		windowRegionUnions &&
		typeof windowRegionUnions === 'object' &&
		Array.isArray(windowRegionUnions.monthly);
	const hasSightings = stateSightings && Object.keys(stateSightings).length > 0;
	if (!hasServerUnions && !hasSightings && playMode !== 'monthly') return [];

	return ['monthly', 'annual', 'decennial']
		.filter((window) => window !== playMode)
		.map((window) => {
			let regionIds;
			if (hasServerUnions && Array.isArray(windowRegionUnions[window])) {
				regionIds = windowRegionUnions[window];
			} else {
				regionIds = regionIdsForWindow(window, {
					visitedStates,
					stateTiers,
					stateSightings,
					currentMonthYear,
				});
				if (regionIds.length === 0) return null;
			}
			const stats = computeScopeProgress(regionIds, scope, denominators);
			return {
				window,
				windowLabel: playModeLabel(window),
				...stats,
			};
		})
		.filter(Boolean);
}

export function formatScopeStatsLine(stats) {
	return `${stats.label} · ${stats.count} / ${stats.denominator} · ${stats.percent}%`;
}

export function formatWindowSummaryLine(summary) {
	return `${summary.windowLabel} · ${summary.count} / ${summary.denominator} · ${summary.percent}%`;
}
